/* Greaser's Alley — product page: variant picker, gallery, zoom, quantity, share,
   sticky add-to-cart, and <product-recommendations> lazy loading.
   Custom elements re-initialise themselves when the theme editor reloads a section. */
(function () {
  'use strict';
  const theme = (window.theme = window.theme || {});
  const money = cents => (theme.formatMoney ? theme.formatMoney(cents) : '$' + (cents / 100).toFixed(2));
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =================================================================== */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const json = this.querySelector('[data-product-json]');
      this.data = json ? JSON.parse(json.textContent) : { variants: [], options: [] };
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('form[data-product-form]');
      this.initPicker();
      this.initGallery();
      this.initZoom();
      this.initQuantity();
      this.initShare();
      this.initSticky();
    }

    disconnectedCallback() {
      if (this._stickyObserver) this._stickyObserver.disconnect();
      if (this._slideObserver) this._slideObserver.disconnect();
    }

    /* ---------------- variant picker ---------------- */
    initPicker() {
      this.picker = this.querySelector('[data-variant-picker]');
      if (!this.picker) return;
      this.picker.addEventListener('change', () => this.onOptionChange());
      this.refreshAvailability(this.selectedOptions());
      // A variant chosen from a link (?variant=) may have its own image further along the gallery
      const v = this.currentVariant();
      if (v && v.featured_media) this.showMedia(v.featured_media.id, false);
    }

    selectedOptions() {
      return [...this.picker.querySelectorAll('fieldset')].map(fs => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    currentVariant() {
      const select = this.form && this.form.querySelector('[data-variant-select]');
      const id = select ? parseInt(select.value, 10) : null;
      return this.data.variants.find(v => v.id === id) || null;
    }

    findVariant(options) {
      return this.data.variants.find(v => v.options.every((o, i) => o === options[i])) || null;
    }

    refreshAvailability(selected) {
      this.picker.querySelectorAll('fieldset').forEach((fs, i) => {
        fs.querySelectorAll('input').forEach(input => {
          const label = fs.querySelector('label[for="' + input.id + '"]');
          const ok = this.data.variants.some(v => v.available && v.options[i] === input.value &&
            v.options.every((o, j) => j === i || o === selected[j]));
          label.classList.toggle('is-unavailable', !ok);
          const sr = label.querySelector('[data-unavailable-label]');
          if (sr) sr.textContent = ok ? '' : ' (sold out)';
        });
        const sel = fs.querySelector('[data-option-selected]');
        const checked = fs.querySelector('input:checked');
        const shown = checked && fs.querySelector('label[for="' + checked.id + '"] [data-value-label]');
        if (sel) sel.textContent = shown ? shown.textContent : (selected[i] || '');
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.refreshAvailability(selected);
      const variant = this.findVariant(selected);
      this.renderVariant(variant);
      if (!variant) return;

      const select = this.form && this.form.querySelector('[data-variant-select]');
      if (select) select.value = variant.id;

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media) this.showMedia(variant.featured_media.id, true);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    renderVariant(variant) {
      const addBtn = this.querySelector('[data-add-button]');
      const addText = this.querySelector('[data-add-text]');
      const sticky = this.querySelector('[data-sticky-add]');
      const stickyPrice = this.querySelector('[data-sticky-price]');
      const price = this.querySelector('[data-price-wrap] [data-price]');
      const save = this.querySelector('[data-save]');
      const stock = this.querySelector('[data-stock]');
      const sku = this.querySelector('[data-sku]');

      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addText) addText.textContent = 'Unavailable';
        if (sticky) { sticky.disabled = true; sticky.textContent = 'Unavailable'; }
        if (stock) stock.innerHTML = '<span class="pstock__dot pstock__dot--out"></span>That combination isn\'t available.';
        return;
      }

      const label = variant.available ? 'Add to cart' : 'Sold out';
      if (addBtn) addBtn.disabled = !variant.available;
      if (addText) addText.textContent = label;
      if (sticky) { sticky.disabled = !variant.available; sticky.textContent = label; }
      if (stickyPrice) stickyPrice.textContent = money(variant.price);

      if (price) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        price.innerHTML = (onSale
          ? '<span class="sr-only">Regular price</span><s>' + money(variant.compare_at_price) + '</s><span class="sr-only">Sale price</span>'
          : '') + money(variant.price);
        if (save) {
          save.hidden = !onSale;
          if (onSale) save.textContent = 'Save ' + Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) + '%';
        }
      }
      if (stock) {
        stock.innerHTML = variant.available
          ? '<span class="pstock__dot"></span>In stock'
          : '<span class="pstock__dot pstock__dot--out"></span>This size is sold out';
      }
      if (sku) {
        sku.hidden = !variant.sku;
        const span = sku.querySelector('span');
        if (span) span.textContent = variant.sku || '';
      }
      const errorBox = this.querySelector('[data-form-error]');
      if (errorBox) errorBox.hidden = true;
    }

    /* ---------------- gallery ---------------- */
    initGallery() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.indexEl = this.querySelector('[data-gallery-index]');

      this.thumbs.forEach(t => t.addEventListener('click', () => this.showMedia(t.dataset.galleryThumb, true)));
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      this.track.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });

      if ('IntersectionObserver' in window) {
        this._slideObserver = new IntersectionObserver(entries => {
          entries.forEach(e => { if (e.isIntersecting) this.setActive(this.slides.indexOf(e.target)); });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach(s => this._slideObserver.observe(s));
      }
    }

    step(dir) {
      const i = Math.max(0, Math.min(this.slides.length - 1, (this.activeIndex || 0) + dir));
      this.scrollToSlide(i, true);
    }

    showMedia(mediaId, smooth) {
      if (!this.slides) return;
      const i = this.slides.findIndex(s => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.scrollToSlide(i, smooth);
    }

    scrollToSlide(i, smooth) {
      const slide = this.slides[i];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      this.setActive(i);
      // pause any video we scrolled away from
      this.slides.forEach((s, j) => { if (j !== i) s.querySelectorAll('video').forEach(v => v.pause()); });
    }

    setActive(i) {
      if (i < 0) return;
      this.activeIndex = i;
      this.thumbs.forEach((t, j) => {
        if (j === i) {
          t.setAttribute('aria-current', 'true');
          const list = t.closest('ul');
          if (list && list.scrollWidth > list.clientWidth) {
            list.scrollTo({ left: t.offsetLeft - list.clientWidth / 2 + t.offsetWidth / 2, behavior: reduce ? 'auto' : 'smooth' });
          }
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (this.indexEl) this.indexEl.textContent = i + 1;
    }

    /* ---------------- zoom ---------------- */
    initZoom() {
      const dialog = this.querySelector('[data-zoom-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const img = document.createElement('img');
      img.setAttribute('data-zoom-img', '');
      dialog.appendChild(img);
      this.querySelectorAll('[data-zoom-src]').forEach(btn => {
        btn.addEventListener('click', () => {
          const source = btn.querySelector('img');
          img.src = btn.dataset.zoomSrc;
          img.alt = source ? source.alt : '';
          dialog.showModal();
          document.body.classList.add('no-scroll');
        });
      });
      const close = () => dialog.close();
      dialog.querySelector('[data-zoom-close]').addEventListener('click', close);
      dialog.addEventListener('click', e => { if (e.target === dialog || e.target === img) close(); });
      dialog.addEventListener('close', () => document.body.classList.remove('no-scroll'));
    }

    /* ---------------- quantity ---------------- */
    initQuantity() {
      const wrap = this.querySelector('[data-qty-product]');
      if (!wrap) return;
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('[data-qty-btn]').forEach(btn => {
        btn.addEventListener('click', () => {
          const min = parseInt(input.min, 10) || 1;
          const max = input.max ? parseInt(input.max, 10) : Infinity;
          const next = (parseInt(input.value, 10) || min) + parseInt(btn.dataset.qtyBtn, 10);
          input.value = Math.max(min, Math.min(max, next));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      // reset to 1 after a successful add so the next add doesn't silently repeat a big number
      document.addEventListener('cart:added', e => {
        if (e.detail && e.detail.form === this.form) input.value = input.min || 1;
      });
    }

    /* ---------------- share ---------------- */
    initShare() {
      const btn = this.querySelector('[data-copy-link]');
      if (!btn) return;
      const status = this.querySelector('[data-copy-status]');
      btn.addEventListener('click', async () => {
        const url = btn.dataset.copyLink;
        try {
          if (navigator.share && matchMedia('(pointer: coarse)').matches) {
            await navigator.share({ url, title: document.title });
            return;
          }
          await navigator.clipboard.writeText(url);
          if (status) { status.textContent = 'Link copied'; setTimeout(() => { status.textContent = ''; }, 2200); }
        } catch (e) { /* share sheet dismissed or clipboard blocked */ }
      });
    }

    /* ---------------- sticky add to cart ---------------- */
    initSticky() {
      const bar = this.querySelector('[data-sticky-atc]');
      const target = this.querySelector('[data-add-button]');
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      const button = bar.querySelector('[data-sticky-add]');
      this._stickyObserver = new IntersectionObserver(([entry]) => {
        // show once the real button has scrolled up out of view, not before we reach it
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (button) button.tabIndex = show ? 0 : -1;
      });
      this._stickyObserver.observe(target);
    }
  }

  /* =================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const load = () => {
        fetch(this.dataset.url)
          .then(r => r.text())
          .then(text => {
            const html = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
            if (html && html.querySelector('.pcard')) {
              this.innerHTML = html.innerHTML;
              this.classList.add('is-loaded');
              if (theme.init) theme.init(this);
            } else {
              const section = this.closest('.shopify-section');
              (section || this).hidden = true;
            }
          })
          .catch(() => { (this.closest('.shopify-section') || this).hidden = true; });
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }

  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
})();
